import React, { useState, useEffect } from 'react';
import { post } from '../../util/api';
import {
	Box,
	Typography,
	Card,
	CardContent,
	Grid,
	Avatar,
	Button,
	Alert,
	Skeleton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	RadioGroup,
	FormControlLabel,
	Radio,
	Paper,
	Chip,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import ErrorIcon from '@mui/icons-material/Error';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PendingIcon from '@mui/icons-material/Pending';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import StarIcon from '@mui/icons-material/Star';
import FireworkIcon from '@mui/icons-material/Celebration';

const EMAIL_TEMPLATES = [
	{
		id: 'maytapi-cart-recovery-payment-declined',
		label: 'İlk Gönderim',
		icon: <ErrorIcon color="error" />,
		name: 'Complete Your Maytapi Subscription',
	},
	{
		id: 'maytapi-cart-recovery-checkout-abandoned',
		label: 'İkinci Gönderim',
		icon: <EmailIcon color="info" />,
		name: 'Still Interested? Your Maytapi Subscription is Waiting',
	},
	{
		id: 'maytapi-cart-recovery-first-discount',
		label: '%10 İndirim',
		icon: <LocalOfferIcon color="success" />,
		name: 'Complete your order today on Maytapi With %10 promo code',
		discount: '10%',
	},
	{
		id: 'maytapi-cart-recovery-second-discount',
		label: '%20 İndirim',
		icon: <FlashOnIcon color="warning" />,
		name: 'Complete your order today on Maytapi With %20 promo code',
		discount: '20%',
	},
	{
		id: 'maytapi-cart-recovery-third-discount',
		label: '%30 İndirim',
		icon: <StarIcon color="primary" />,
		name: "Complete your order today on Maytapi With %30 promo code",
		discount: '30%',
	},
	{
		id: 'maytapi-cart-recovery-last-discount',
		label: '%40 İndirim',
		icon: <FireworkIcon color="secondary" />,
		name: 'Complete your order today on Maytapi With %40 promo code',
		discount: '40%',
	},
];

const CartLogDetails = () => {
	const [state, setState] = useState({
		loading: true,
		userDetails: null,
		error: null,
		uid: null,
		emailDialog: {
			open: false,
			selectedTemplate: '',
		},
		buttonloading: false,
	});

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const uid = urlParams.get('uid');

		if (uid) {
			setState((prev) => ({ ...prev, uid }));
			fetchUserDetails(uid);
		} else {
			setState((prev) => ({
				...prev,
				loading: false,
				error: 'User ID not found',
			}));
		}
	}, []);

	const fetchUserDetails = async (uid) => {
		try {
			setState((prev) => ({ ...prev, loading: true }));

			const response = await post('/maytapi/admin/abandoned-cart', {
				page: 0,
				limit: 1,
				filter: {
					uid: uid,
				},
			});

			if (response.data.success && response.data.result && response.data.result.length > 0) {
				setState((prev) => ({
					...prev,
					loading: false,
					userDetails: {
						...response.data.result[0],
						sent_emails: response.data.result[0].sent_emails || [],
					},
				}));
			} else {
				throw new Error('Kullanıcı bulunamadı');
			}
		} catch (error) {
			console.error('Error fetching user details:', error);
			setState((prev) => ({
				...prev,
				loading: false,
				error: error.message,
			}));
		}
	};

	const formatDateTime = (dateString) => {
		if (!dateString) return 'N/A';

		const date = new Date(dateString);
		const now = new Date();
		const diffInMs = now - date;

		const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

		if (diffInMinutes < 1) {
			return 'Az önce';
		} else if (diffInMinutes < 60) {
			return `${diffInMinutes} dakika önce`;
		} else if (diffInMinutes < 1440) {
			const hours = Math.floor(diffInMinutes / 60);
			const minutes = diffInMinutes % 60;

			if (minutes === 0) {
				return `${hours} saat önce`;
			} else {
				return `${hours} saat ${minutes} dakika önce`;
			}
		} else {
			const days = Math.floor(diffInMinutes / 1440);
			return `${days} gün önce`;
		}
	};

	const formatDateTimeForPlan = (dateString) => {
		if (!dateString) return 'N/A';
		const date = new Date(dateString);
		return date.toLocaleDateString('tr-TR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const openEmailDialog = () => {
		const suggestedTemplate =
			userDetails?.label === 'paddle-payment-declined' ? 'maytapi-cart-recovery-payment-declined' : 'maytapi-cart-recovery-checkout-abandoned';

		setState((prev) => ({
			...prev,
			emailDialog: {
				open: true,
				selectedTemplate: suggestedTemplate,
			},
		}));
	};

	const openEmailDialogWithTemplate = (templateId) => {
		setState((prev) => ({
			...prev,
			emailDialog: {
				open: true,
				selectedTemplate: templateId,
			},
		}));
	};

	const closeEmailDialog = () => {
		setState((prev) => ({
			...prev,
			emailDialog: {
				open: false,
				selectedTemplate: '',
			},
		}));
	};

	const handleTemplateSelect = (templateId) => {
		setState((prev) => ({
			...prev,
			emailDialog: {
				...prev.emailDialog,
				selectedTemplate: templateId,
			},
		}));
	};

	const sendRecoveryEmail = async () => {
		const { emailDialog } = state;
		const { selectedTemplate } = emailDialog;

		try {
			setState((prev) => ({ ...prev, buttonloading: true }));

			const emails = [userDetails.email];
			const extraEmail = userDetails.extra?.email;

			if (extraEmail && extraEmail !== userDetails.email) {
				emails.push(extraEmail);
			}

			const promises = emails.map((emailAddress) =>
				post('/mailer/cart-recovery', {
					uid: userDetails.uid,
					email: emailAddress,
					template_type: selectedTemplate,
					extra: {
						user_name: emailAddress,
						last_event_label: userDetails.label || 'checkout-close',
						error_message: '',
						product_name:
							(userDetails.extra &&
								userDetails.extra.checkout &&
								userDetails.extra.checkout.prices &&
								userDetails.extra.checkout.prices.customer &&
								userDetails.extra.checkout.prices.customer.items &&
								userDetails.extra.checkout.prices.customer.items[0] &&
								userDetails.extra.checkout.prices.customer.items[0].name) ||
							'Maytapi Phone Service',
						price:
							(userDetails.extra &&
								userDetails.extra.checkout &&
								userDetails.extra.checkout.prices &&
								userDetails.extra.checkout.prices.customer &&
								userDetails.extra.checkout.prices.customer.unit &&
								`$${userDetails.extra.checkout.prices.customer.unit}`) ||
							'$29.90',
						currency:
							(userDetails.extra &&
								userDetails.extra.checkout &&
								userDetails.extra.checkout.prices &&
								userDetails.extra.checkout.prices.customer &&
								userDetails.extra.checkout.prices.customer.currency) ||
							'USD',
						checkout_url:
							userDetails.extra && userDetails.extra.checkout && userDetails.extra.checkout.id
								? `https://checkout.paddle.com/checkout/complete?checkout=${userDetails.extra.checkout.id}`
								: 'https://console.maytapi.com/subscription',
					},
				})
			);

			const responses = await Promise.all(promises);
			const successfulSends = responses.filter((response) => response.data.success);
			const failedSends = responses.filter((response) => !response.data.success);

			if (successfulSends.length > 0) {
				const emailCount = successfulSends.length;
				const totalEmails = responses.length;

				if (failedSends.length === 0) {
					alert(
						`"${
							EMAIL_TEMPLATES.find((t) => t.id === selectedTemplate)?.label
						}" template'i ile ${emailCount} adrese recovery email başarıyla gönderildi!`
					);
				} else {
					alert(
						`"${
							EMAIL_TEMPLATES.find((t) => t.id === selectedTemplate)?.label
						}" template'i ile ${emailCount}/${totalEmails} adrese recovery email gönderildi. ${failedSends.length} email gönderilemedi.`
					);
				}

				closeEmailDialog();
				fetchUserDetails(userDetails.uid);
			} else {
				alert('Email gönderme hatası: ' + failedSends[0].data.message);
			}
		} catch (error) {
			console.error('Email sending error:', error);
			alert('Email gönderme hatası: ' + error.message);
		} finally {
			setState((prev) => ({ ...prev, buttonloading: false }));
		}
	};

	const { loading, userDetails, error, emailDialog, buttonloading } = state;

	if (loading) {
		return (
			<Box sx={{ p: 3 }}>
				<Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 2 }} />
				<Grid container spacing={3}>
					{[1, 2, 3, 4].map((i) => (
						<Grid item xs={12} sm={6} key={i}>
							<Skeleton variant="rectangular" width="100%" height={200} />
						</Grid>
					))}
				</Grid>
			</Box>
		);
	}

	if (error) {
		return (
			<Box sx={{ p: 3 }}>
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
				<Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => window.history.back()}>
					Geri Dön
				</Button>
			</Box>
		);
	}

	const {
		email,
		name,
		payment_failures,
		checkout_closes,
		last_attempt_price,
		last_attempt_currency,
		recovery_emails_sent,
		sent_emails,
		last_attempt_date,
		potential_value,
		extra,
	} = userDetails;

	const extraEmail = extra && extra.email;
	const totalErrors = (payment_failures || 0) + (checkout_closes || 0);
	const potentialValue = potential_value || last_attempt_price || 0;
	const totalEmailsSent = sent_emails ? sent_emails.length : recovery_emails_sent || 0;

	const getEmailStatus = (templateId) => {
		if (!sent_emails || sent_emails.length === 0) return null;

		const sentEmail = sent_emails.find((email) => email.extra?.template_type === templateId);

		return sentEmail
			? {
					sent: true,
					date: sentEmail.date,
					formattedDate: formatDateTimeForPlan(sentEmail.date),
			  }
			: null;
	};

	const completedSteps = EMAIL_TEMPLATES.filter((template) => {
		const emailStatus = getEmailStatus(template.id);
		return emailStatus?.sent;
	}).length;

	const nextStep = EMAIL_TEMPLATES.find((template) => {
		const emailStatus = getEmailStatus(template.id);
		return !emailStatus?.sent;
	});

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
				<Typography variant="h4" sx={{ fontWeight: 'bold' }}>
					Kullanıcı Detayları
				</Typography>
			</Box>

			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
								<PersonIcon />
							</Avatar>
							<Box>
								<Typography variant="h6">{name || email}</Typography>
								<Typography variant="body2" color="text.secondary">
									{email}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									Extra: {extraEmail}
								</Typography>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<Avatar
								sx={{
									bgcolor:
										userDetails.risk_level === 'high'
											? 'error.main'
											: userDetails.risk_level === 'medium'
											? 'warning.main'
											: 'success.main',
									mr: 1,
								}}
							>
								{userDetails.risk_level === 'high' ? (
									<ErrorIcon />
								) : userDetails.risk_level === 'medium' ? (
									<WarningIcon />
								) : (
									<CheckCircleIcon />
								)}
							</Avatar>
							<Typography variant="body2" color="text.secondary">
								{userDetails.risk_level === 'high' ? 'Yüksek Risk' : userDetails.risk_level === 'medium' ? 'Orta Risk' : 'Düşük Risk'}
							</Typography>
						</Box>
					</Box>
				</CardContent>
			</Card>

			<Grid container spacing={3} sx={{ mb: 3 }}>
				<Grid item xs={12} sm={6}>
					<Card>
						<CardContent>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
									<ErrorIcon />
								</Avatar>
								<Box>
									<Typography variant="h6">{totalErrors}</Typography>
									<Typography variant="body2" color="text.secondary">
										Toplam Hata
									</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6}>
					<Card>
						<CardContent>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
									<AttachMoneyIcon />
								</Avatar>
								<Box>
									<Typography variant="h6">
										{last_attempt_currency === 'USD' && '$'}
										{potentialValue ? potentialValue.toFixed(2) : '0.00'}
										{last_attempt_currency !== 'USD' && ` ${last_attempt_currency || 'USD'}`}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Potansiyel Değer
									</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6}>
					<Card>
						<CardContent>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
									<EmailIcon />
								</Avatar>
								<Box>
									<Typography variant="h6">{totalEmailsSent}</Typography>
									<Typography variant="body2" color="text.secondary">
										Gönderilen Toplam Mail
									</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6}>
					<Card>
						<CardContent>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
									<AccessTimeIcon />
								</Avatar>
								<Box>
									<Typography variant="h6">{formatDateTime(last_attempt_date)}</Typography>
									<Typography variant="body2" color="text.secondary">
										Son Aktivite
									</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
						Recovery Stratejisi
					</Typography>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #bbdefb' }}>
							<Typography variant="subtitle1" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
								Son Hata Türü
							</Typography>
							<Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
								{userDetails.label === 'paddle-payment-declined'
									? 'Banka reddi'
									: userDetails.label === 'checkout-close'
									? 'Checkout kapatıldı'
									: 'Bilinmeyen işlem'}
							</Typography>
						</Box>

						<Box sx={{ p: 2, bgcolor: '#e8f5e8', borderRadius: 1, border: '1px solid #c8e6c9' }}>
							<Typography variant="subtitle1" color="success.main" gutterBottom sx={{ fontWeight: 'bold' }}>
								Tamamlanan Adımlar
							</Typography>
							<Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
								{completedSteps} / {EMAIL_TEMPLATES.length}
							</Typography>
						</Box>

						<Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc02' }}>
							<Typography variant="subtitle1" color="warning.main" gutterBottom sx={{ fontWeight: 'bold' }}>
								Sonraki Adım
							</Typography>
							<Typography variant="body2" color="warning.main" sx={{ fontWeight: 'medium' }}>
								{nextStep ? nextStep.name : 'Tüm adımlar tamamlandı'}
							</Typography>
							<Typography variant="caption" color="warning.main">
								{nextStep ? nextStep.label : 'Hiçbir adım kalmadı'}
							</Typography>
						</Box>
					</Box>
				</CardContent>
			</Card>

			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
						Recovery Planı
					</Typography>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						{EMAIL_TEMPLATES.map((template, index) => {
							const emailStatus = getEmailStatus(template.id);
							const isSent = emailStatus?.sent;
							const isFirstUnsent = !isSent && !EMAIL_TEMPLATES.slice(0, index).some((t) => !getEmailStatus(t.id)?.sent);

							return (
								<Box
									key={template.id}
									sx={{
										display: 'flex',
										alignItems: 'center',
										p: 2,
										border: '1px solid #e0e0e0',
										borderRadius: 1,
										bgcolor: isSent ? '#f1f8e9' : '#fff',
										'&:hover': { bgcolor: isSent ? '#e8f5e8' : '#f9f9f9' },
									}}
								>
									<Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
										<Avatar
											sx={{
												bgcolor: isSent ? 'success.main' : isFirstUnsent ? 'warning.main' : 'grey.300',
												color: isSent ? 'white' : isFirstUnsent ? 'white' : 'grey.600',
												width: 32,
												height: 32,
												mr: 1,
											}}
										>
											{isSent ? <CheckCircleIcon /> : isFirstUnsent ? <AccessTimeIcon /> : <PendingIcon />}
										</Avatar>
									</Box>

									<Box sx={{ flex: 1 }}>
										<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
											{template.name}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{isSent ? `Gönderildi: ${emailStatus.formattedDate}` : 'Henüz gönderilmedi'}
										</Typography>
									</Box>

									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										{isSent && <Chip size="medium" label="Gönderildi" color="success" variant="filled" />}
										{isFirstUnsent && <Chip size="small" label="Sırada" color="warning" variant="filled" />}

										{template.discount && <Chip size="small" label={`${template.discount} İndirim`} color="error" variant="filled" />}

										{isFirstUnsent && (
											<Button
												size="small"
												variant="contained"
												color="primary"
												startIcon={<SendIcon />}
												onClick={() => openEmailDialogWithTemplate(template.id)}
												disabled={buttonloading}
												sx={{ ml: 1 }}
											>
												Mail Gönder
											</Button>
										)}
									</Box>
								</Box>
							);
						})}
					</Box>
				</CardContent>
			</Card>

			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
						Kullanıcı Bilgileri
					</Typography>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								Email
							</Typography>
							<Typography variant="body1">{email}</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Extra: {extraEmail}
							</Typography>
						</Box>

						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								Plan
							</Typography>
							<Typography variant="body1">
								{userDetails?.extra?.checkout?.prices?.customer?.items?.[0]?.name || 'Plan bilgisi bulunamadı'}
							</Typography>
						</Box>

						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								Fiyat
							</Typography>
							<Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
								{last_attempt_currency === 'USD' && '$'}
								{userDetails?.extra?.checkout?.prices?.customer?.unit || '0.00'}
								{last_attempt_currency !== 'USD' && ` ${last_attempt_currency || 'USD'}`}
							</Typography>
						</Box>

						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								Durum
							</Typography>
							<Box
								sx={{
									display: 'inline-block',
									px: 1.5,
									py: 0.5,
									borderRadius: 1,
									bgcolor: userDetails.risk_level === 'high' ? '#ffebee' : '#e8f5e8',
									color: userDetails.risk_level === 'high' ? 'error.main' : 'success.main',
									fontSize: '0.875rem',
									fontWeight: 'medium',
								}}
							>
								{userDetails.risk_level === 'high'
									? 'Yüksek Risk'
									: userDetails.risk_level === 'medium'
									? 'Orta Risk'
									: userDetails.risk_level === 'low'
									? 'Düşük Risk'
									: 'Belirsiz'}
							</Box>
						</Box>

						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								Son Aktivite
							</Typography>
							<Typography variant="body1">{formatDateTime(last_attempt_date)}</Typography>
						</Box>

						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								Son İşlem
							</Typography>
							<Typography variant="body1">
								{userDetails.label === 'paddle-payment-declined'
									? 'Banka reddi'
									: userDetails.label === 'checkout-close'
									? 'Checkout kapatıldı'
									: 'Bilinmeyen işlem'}
							</Typography>
						</Box>
					</Box>
				</CardContent>
			</Card>

			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
						<Typography variant="h5" sx={{ fontWeight: 'bold' }}>
							Email Geçmişi ({(sent_emails && sent_emails.length) || 0})
						</Typography>
						<Button variant="contained" size="small" startIcon={<EmailIcon />} onClick={openEmailDialog} disabled={buttonloading}>
							Yeni Email
						</Button>
					</Box>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: '1fr 2fr 1fr 1fr',
								gap: 2,
								p: 2,
								bgcolor: '#f5f5f5',
								borderRadius: 1,
								fontWeight: 'bold',
							}}
						>
							<Typography variant="subtitle2" color="text.secondary">
								TARİH
							</Typography>
							<Typography variant="subtitle2" color="text.secondary">
								TEMPLATE
							</Typography>
							<Typography variant="subtitle2" color="text.secondary">
								EVENT
							</Typography>
							<Typography variant="subtitle2" color="text.secondary">
								DURUM
							</Typography>
						</Box>

						{sent_emails &&
							sent_emails.map((email, index) => {
								const emailDate = new Date(email.date);
								const formattedDate = emailDate.toLocaleDateString('tr-TR', {
									day: '2-digit',
									month: '2-digit',
									year: '2-digit',
									hour: '2-digit',
									minute: '2-digit',
								});

								const getTemplateInfo = (templateType) => {
									if (templateType === 'maytapi-cart-recovery-payment-declined') {
										return { text: 'İlk Gönderim', color: 'error.main', bgcolor: '#ffebee' };
									} else if (templateType === 'maytapi-cart-recovery-checkout-abandoned') {
										return { text: 'İkinci Gönderim', color: 'info.main', bgcolor: '#e3f2fd' };
									} else if (templateType === 'maytapi-cart-recovery-first-discount') {
										return { text: '%10 İndirim', color: 'success.main', bgcolor: '#e8f5e8' };
									} else if (templateType === 'maytapi-cart-recovery-second-discount') {
										return { text: '%20 İndirim', color: 'warning.main', bgcolor: '#fff3e0' };
									} else if (templateType === 'maytapi-cart-recovery-third-discount') {
										return { text: '%30 İndirim', color: 'primary.main', bgcolor: '#e3f2fd' };
									} else if (templateType === 'maytapi-cart-recovery-last-discount') {
										return { text: '%40 İndirim', color: 'secondary.main', bgcolor: '#f3e5f5' };
									} else {
										return { text: 'Otomatik', color: 'info.main', bgcolor: '#e3f2fd' };
									}
								};

								const templateInfo = getTemplateInfo(email.extra?.template_type || 'auto');

								return (
									<Box
										key={index}
										sx={{
											display: 'grid',
											gridTemplateColumns: '1fr 2fr 1fr 1fr',
											gap: 2,
											p: 2,
											borderBottom: '1px solid #e0e0e0',
											'&:hover': { bgcolor: '#f9f9f9' },
										}}
									>
										<Typography variant="body2">{formattedDate}</Typography>

										<Box sx={{ display: 'flex', alignItems: 'center' }}>
											<Box
												sx={{
													px: 1,
													py: 0.5,
													borderRadius: 1,
													bgcolor: templateInfo.bgcolor,
													color: templateInfo.color,
													fontSize: '0.75rem',
													fontWeight: 'medium',
												}}
											>
												⚠️ {templateInfo.text}
											</Box>
										</Box>

										<Box sx={{ display: 'flex', alignItems: 'center' }}>
											<Box
												sx={{
													px: 1,
													py: 0.5,
													borderRadius: 1,
													bgcolor: '#fff3e0',
													color: 'warning.main',
													fontSize: '0.75rem',
													border: '1px solid #ffcc02',
												}}
											>
												Checkout Kapatıldı
											</Box>
										</Box>

										<Box sx={{ display: 'flex', alignItems: 'center' }}>
											<Box
												sx={{
													px: 1,
													py: 0.5,
													borderRadius: 1,
													bgcolor: '#e8f5e8',
													color: 'success.main',
													fontSize: '0.75rem',
													display: 'flex',
													alignItems: 'center',
													gap: 0.5,
												}}
											>
												✅ Gönderildi
											</Box>
										</Box>
									</Box>
								);
							})}

						{(!sent_emails || sent_emails.length === 0) && (
							<Box sx={{ textAlign: 'center', py: 4 }}>
								<Typography variant="body2" color="text.secondary">
									Henüz email gönderilmemiş
								</Typography>
							</Box>
						)}
					</Box>
				</CardContent>
			</Card>

			<Dialog open={emailDialog.open} onClose={closeEmailDialog} maxWidth="md" fullWidth>
				<DialogTitle>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<EmailIcon />
						<Typography variant="h6">Recovery Email Gönder</Typography>
					</Box>
				</DialogTitle>
				<DialogContent>
					<Box sx={{ mb: 3 }}>
						<Typography variant="body2" color="text.secondary">
							{userDetails?.email} adresine göndermek için template seçin
						</Typography>
					</Box>

					<RadioGroup value={emailDialog.selectedTemplate} onChange={(e) => handleTemplateSelect(e.target.value)}>
						{EMAIL_TEMPLATES.map((template) => (
							<Paper key={template.id} elevation={1} sx={{ p: 2, mb: 2 }}>
								<FormControlLabel
									value={template.id}
									control={<Radio />}
									label={
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
											{template.icon}
											<Box sx={{ flex: 1 }}>
												<Typography variant="body1" sx={{ fontWeight: 'medium' }}>
													{template.name}
												</Typography>
											</Box>
										</Box>
									}
									sx={{ margin: 0, width: '100%' }}
								/>
							</Paper>
						))}
					</RadioGroup>

					{emailDialog.selectedTemplate && (
						<Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
							<Typography variant="body2" color="primary.contrastText">
								<strong>Seçilen Template:</strong> {EMAIL_TEMPLATES.find((t) => t.id === emailDialog.selectedTemplate)?.name}
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeEmailDialog}>İptal</Button>
					<Button
						onClick={sendRecoveryEmail}
						variant="contained"
						disabled={buttonloading || !emailDialog.selectedTemplate}
						startIcon={buttonloading ? <HourglassBottomIcon /> : <SendIcon />}
					>
						{buttonloading ? 'Gönderiliyor...' : 'Email Gönder'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default CartLogDetails;
